import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Game, SportType } from '@/types';
import {
  LOCAL_APP_STATE_KEY,
  emptyLocalAppState,
  parseLocalAppState,
  serializeLocalAppState,
  type LocalAppState,
  type ReplayPlanItem,
} from '@/lib/localAppState';
import { cancelReminder } from '@/lib/platform/reminders';

interface LocalAppStateContextValue {
  state: LocalAppState;
  completeOnboarding: (sports: SportType[]) => void;
  skipOnboarding: () => void;
  saveGame: (game: Game) => void;
  removePlanItem: (id: string) => Promise<void>;
  updatePlanItem: (id: string, patch: Partial<Pick<ReplayPlanItem, 'reminderId' | 'reminderAt'>>) => void;
  resetAll: () => Promise<void>;
}

const LocalAppStateContext = createContext<LocalAppStateContextValue | null>(null);

export function LocalAppStateProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<LocalAppState>(() => {
    if (typeof window === 'undefined') return emptyLocalAppState();
    return parseLocalAppState(window.localStorage.getItem(LOCAL_APP_STATE_KEY));
  });

  useEffect(() => {
    window.localStorage.setItem(LOCAL_APP_STATE_KEY, serializeLocalAppState(state));
  }, [state]);

  const value = useMemo<LocalAppStateContextValue>(() => ({
    state,
    completeOnboarding: (preferredSports) => setState((current) => ({
      ...current,
      onboardingStatus: 'complete',
      preferredSports,
    })),
    skipOnboarding: () => setState((current) => ({ ...current, onboardingStatus: 'skipped' })),
    saveGame: (game) => setState((current) => {
      if (current.replayPlan.some((item) => item.id === game.id)) return current;
      const item: ReplayPlanItem = {
        id: game.id,
        sportType: game.sportType,
        utcDatetime: game.utcDatetime,
        homeTeamName: `${game.homeTeam.city} ${game.homeTeam.name}`.trim(),
        awayTeamName: `${game.awayTeam.city} ${game.awayTeam.name}`.trim(),
        competition: game.competition,
        arena: game.arena === 'Unknown' ? undefined : game.arena,
        intent: 'watch-later',
        savedAt: new Date().toISOString(),
      };
      return { ...current, replayPlan: [item, ...current.replayPlan] };
    }),
    removePlanItem: async (id) => {
      const item = state.replayPlan.find((candidate) => candidate.id === id);
      if (item?.reminderId) await cancelReminder(item.reminderId);
      setState((current) => ({ ...current, replayPlan: current.replayPlan.filter((candidate) => candidate.id !== id) }));
    },
    updatePlanItem: (id, patch) => setState((current) => ({
      ...current,
      replayPlan: current.replayPlan.map((item) => item.id === id ? { ...item, ...patch } : item),
    })),
    resetAll: async () => {
      await Promise.all(state.replayPlan.map((item) => item.reminderId ? cancelReminder(item.reminderId) : Promise.resolve()));
      setState(emptyLocalAppState());
    },
  }), [state]);

  return <LocalAppStateContext.Provider value={value}>{children}</LocalAppStateContext.Provider>;
}

export function useLocalAppState(): LocalAppStateContextValue {
  const context = useContext(LocalAppStateContext);
  if (!context) throw new Error('useLocalAppState must be used within LocalAppStateProvider');
  return context;
}

